import { Subscription } from 'rxjs';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { SelectItem } from 'primeng/api';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { PRIORTIES_6 } from 'projects/core/src/lib/constants';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { ROWS_PER_PAGE } from 'projects/admin/src/app/shared/constants';

class CustomTemplateModel extends TemplateModel {
  control_onsite: UntypedFormControl = new UntypedFormControl(false); // tslint:disable-line: variable-name
  control_virtual: UntypedFormControl = new UntypedFormControl(false); // tslint:disable-line: variable-name
  control_prio: UntypedFormControl = new UntypedFormControl(null); // tslint:disable-line: variable-name
  control_tags: UntypedFormControl = new UntypedFormControl([]); // tslint:disable-line: variable-name
}

enum ViewMode {
  Admin = 'admin',
  Profile = 'profile'
}

@Component({
  selector: 'app-user-templates',
  templateUrl: './user-templates.component.html',
  styleUrls: ['./user-templates.component.scss']
})
export class UserTemplatesComponent extends BaseForm implements OnInit, OnChanges, OnDestroy {

  @Input() user: UserModel = null;
  @Input() viewMode: ViewMode = ViewMode.Admin;
  @Output() setEdit = new EventEmitter<string>();

  public loading = false;
  public templates: Array<CustomTemplateModel> = [];
  public priorities: Array<SelectItem> = [];
  public rowsPerPage = ROWS_PER_PAGE;

  public editRight = 'admin.user.edit';

  public form = this.fb.group({});
  public ViewMode = ViewMode;
  private subscriptions: Array<Subscription> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private transloco: TranslocoService,
    private auth: AuthStateService,
    private helper: HelperService
  ) {
    super();
  }

  ngOnInit(): void {
    this.priorities = FormHelper.getSelectList(PRIORTIES_6, '-');
    this.setRights();
    this.loading = true;
    this.api.getModels('templates').subscribe({
      next: (result: any) => {
        if (result.data?.length > 0) {
          this.templates = result.data.filter((template: any) => {
            return template.status === 'template';
          }).map((template: any) => {
            const temp = new CustomTemplateModel().deserialize(template);
            this.form.addControl(temp.name + '_onsite', temp.control_onsite);
            this.form.addControl(temp.name + '_virtual', temp.control_virtual);
            this.form.addControl(temp.name + '_prio', temp.control_prio);
            this.form.addControl(temp.name + '_tags', temp.control_tags);
            if (temp.onsite !== true) {
              temp.control_onsite.disable();
            }
            if (temp.virtual !== true) {
              temp.control_virtual.disable();
            }
            if (!template.tags ||  template.tags?.length === 0) {
              temp.control_tags.disable();
            }
            this.subscriptions.push(temp.control_onsite.valueChanges.subscribe((value: any) => {
              this.setFieldSettings(temp);
            }));
            this.subscriptions.push(temp.control_virtual.valueChanges.subscribe((value: any) => {
              this.setFieldSettings(temp);
            }));
            return temp;
          }).sort((a: CustomTemplateModel, b: CustomTemplateModel) => {
            return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
          });
        }
        this.setTemplates();
      },
      error: (error: any) => {}
    }).add(() => {
      this.loading = false;
    });
  }

  ngOnChanges(): void {
    this.setTemplates();
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        templates: this.getTemplatesPayload()
      };
      const request = (this.viewMode === ViewMode.Profile) ? this.api.saveMyTemplates(payload) : this.api.saveUserTemplates(this.user.id, payload);
      request.subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved'), this.transloco.translate('common.saved'));
          this.user.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  private setRights(): void {
    if (this.viewMode === ViewMode.Profile && this.auth.hasRole('moderator')) {
      this.editRight = null;
    }
  }

  protected startEdit(): void {
    if (this.auth.hasRight(this.editRight)) {
      if (this.user !== null) {
        this.form.patchValue({});
      }
      this.setEdit.emit('templates');
      super.startEdit();
      this.ui.setFormFocus();
    }
  }

  protected stopEdit(): void {
    this.form.reset();
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
    this.setTemplates();
  }

  private setTemplates(): void {
    if (this.user !== null) {
      this.templates.forEach((template: CustomTemplateModel) => {
        const found = this.user.templates?.find((actual: CustomTemplateModel) => {
          return actual.id === template.id;
        });
        template.control_onsite.setValue(found?.pivot.onsite === true ? true : false);
        template.control_virtual.setValue(found?.pivot.virtual === true ? true : false);
        template.control_prio.setValue((found?.pivot.prio) ? found?.pivot.prio.toString() : null);
        const commonTags = found?.pivot?.tags?.filter((tag: string) => (template.tags || []).includes(tag));
        template.control_tags.setValue(commonTags || []);
      });
    }
  }

  private getTemplatesPayload(): any {
    const templates: any = {};
    this.templates.forEach((template: CustomTemplateModel) => {
      if (template.control_onsite.value === true || template.control_virtual.value === true) {
        templates[template.id] = {
          onsite: (template.onsite === true) ? template.control_onsite.value : false,
          virtual: (template.virtual === true) ? template.control_virtual.value : false,
          prio: (template.control_onsite.value === true || template.control_virtual.value === true) ? template.control_prio.value : null,
          tags: template.control_tags.value
        };
      }
    });
    return templates;
  }

  private setFieldSettings(template: CustomTemplateModel): void {
    if (template.control_onsite.value === true || template.control_virtual.value === true) {
      template.control_prio.enable();
      template.control_prio.setValue('1');
      if (template.tags?.length > 0) {
        template.control_tags.enable();
        template.control_tags.setValue(template.tags);
      }
    } else {
      template.control_prio.disable();
      template.control_prio.setValue(null);
      template.control_tags.disable();
      template.control_tags.setValue([]);
    }
  }

  public showPriority(): boolean {
    return this.viewMode === ViewMode.Admin && (this.user.isModerator || this.user.isExternalModerator)
  }

  public showTags(): boolean {
    return this.auth.hasFeature('templates.tags');
  }

  public showNotes(): boolean {
    return this.viewMode === ViewMode.Profile && this.user.isObserver;
  }

}
